import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

/** Warna penanda tempat, sama dengan yang dipakai pada halaman awal. */
export type Accent = 'kelas' | 'jasa' | 'proyek' | 'sayembara' | 'lowongan' | 'talenta'

const accentStyles: Record<Accent, { chip: string; glow: string }> = {
  kelas: { chip: 'bg-kelas-soft text-kelas', glow: 'from-kelas/18' },
  jasa: { chip: 'bg-jasa-soft text-jasa', glow: 'from-jasa/18' },
  proyek: { chip: 'bg-proyek-soft text-proyek', glow: 'from-proyek/18' },
  sayembara: { chip: 'bg-sayembara-soft text-sayembara', glow: 'from-sayembara/18' },
  lowongan: { chip: 'bg-lowongan-soft text-lowongan', glow: 'from-lowongan/18' },
  talenta: { chip: 'bg-talenta-soft text-talenta', glow: 'from-talenta/18' },
}

/**
 * Kepala halaman untuk tiap model kerja.
 *
 * Cahaya berwarna di belakang judul memakai warna bagian yang sedang dibuka,
 * sehingga pengguna mengenali tempatnya sebelum sempat membaca judul. Warna
 * hanya menguatkan judul yang sudah tertulis, tidak menggantikannya.
 */
export function PageIntro({
  accent,
  eyebrow,
  icon,
  title,
  description,
  actions,
  className,
}: {
  accent: Accent
  eyebrow: string
  icon?: ReactNode
  title: string
  description?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  const style = accentStyles[accent]

  return (
    <header className={cn('relative isolate', className)}>
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 -top-10 -z-10 h-52 bg-gradient-to-b to-transparent blur-2xl',
          style.glow,
        )}
      />

      <div className="rise flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <span className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold', style.chip)}>
            {icon}
            {eyebrow}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">{title}</h1>
          {description ? <p className="mt-2 text-muted">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </div>
    </header>
  )
}
